import React, { useState, useEffect } from 'react';
import { Line, Pie, Bar } from 'react-chartjs-2';
import { formatBytes, prepareMemoryChartData } from './metricsHelpers';
import { getDetailedMemoryBreakdown } from '../../services/metricsService';

const MemoryMonitorTab = ({ memoryStats, gcTriggering, handleGarbageCollection }) => {
  const [detailedBreakdown, setDetailedBreakdown] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  const [error, setError] = useState(null);

  // Fetch detailed breakdown on mount and periodically
  useEffect(() => {
    fetchDetailedBreakdown();
    const interval = setInterval(fetchDetailedBreakdown, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const fetchDetailedBreakdown = async () => {
    try {
      setLoadingBreakdown(true);
      setError(null);
      const response = await getDetailedMemoryBreakdown();
      if (response.success) {
        setDetailedBreakdown(response.data);
      }
    } catch (err) {
      console.error('Error fetching detailed breakdown:', err);
      setError('Failed to load detailed memory breakdown');
    } finally {
      setLoadingBreakdown(false);
    }
  };

  // Prepare pie chart data for component breakdown
  const preparePieChartData = () => {
    if (!detailedBreakdown?.breakdown?.components) return null;

    const components = detailedBreakdown.breakdown.components;
    return {
      labels: [
        'TDLib Native (C++)',
        'Node.js Heap',
        'TD.binlog Files',
        'Database Pools',
        'Caches',
        'Node External',
        'Other'
      ],
      datasets: [{
        data: [
          components.tdlibClientsEstimate || 0,  // Add TDLib native memory to pie chart
          components.nodeHeap || 0,
          components.tdlibSessions || 0,
          components.databasePools || 0,
          components.caches || 0,
          components.nodeExternal || 0,
          components.other || 0
        ],
        backgroundColor: [
          '#DC2626', // Dark Red for TDLib (biggest consumer)
          '#3B82F6', // Blue
          '#EF4444', // Red
          '#F59E0B', // Amber
          '#10B981', // Green
          '#8B5CF6', // Purple
          '#6B7280'  // Gray
        ],
        borderWidth: 1
      }]
    };
  };

  // Prepare bar chart data for top memory consumers
  const prepareBarChartData = () => {
    if (!detailedBreakdown?.breakdown?.topMemoryConsumers) return null;

    const consumers = detailedBreakdown.breakdown.topMemoryConsumers;
    return {
      labels: consumers.map(c => c.account || c.apiId),
      datasets: [{
        label: 'Total Size (MB)',
        data: consumers.map(c => c.sizeMB || 0),
        backgroundColor: consumers.map(c => {
          if (c.status === 'critical') return '#EF4444';
          if (c.status === 'warning') return '#F59E0B';
          return '#10B981';
        }),
        borderWidth: 1
      }, {
        label: 'TD.binlog (MB)',
        data: consumers.map(c => c.binlogMB || 0),
        backgroundColor: consumers.map(c => {
          if (c.status === 'critical') return '#DC2626';
          if (c.status === 'warning') return '#D97706';
          return '#059669';
        }),
        borderWidth: 1
      }]
    };
  };

  // Prepare line chart for component history
  const prepareComponentHistoryChart = () => {
    if (!detailedBreakdown?.componentHistory || detailedBreakdown.componentHistory.length === 0) {
      return null;
    }

    const history = detailedBreakdown.componentHistory;
    return {
      labels: history.map((_, index) => `T-${history.length - index - 1}`),
      datasets: [
        {
          label: 'Node Heap',
          data: history.map(h => h.components?.nodeHeap || 0),
          borderColor: '#3B82F6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          tension: 0.1
        },
        {
          label: 'TD.binlog',
          data: history.map(h => h.components?.tdlibSessions || 0),
          borderColor: '#EF4444',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          tension: 0.1
        },
        {
          label: 'DB Pools',
          data: history.map(h => h.components?.databasePools || 0),
          borderColor: '#F59E0B',
          backgroundColor: 'rgba(245, 158, 11, 0.1)',
          tension: 0.1
        },
        {
          label: 'Total RAM',
          data: history.map(h => h.totalRAM || 0),
          borderColor: '#000000',
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          tension: 0.1,
          borderWidth: 2
        }
      ]
    };
  };

  const getAlertLevel = (level) => {
    switch(level) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'critical':
        return 'bg-red-100 text-red-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-green-100 text-green-800';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-700">Memory Monitor - Detailed Analysis</h3>
        <div className="flex gap-2">
          <button
            onClick={fetchDetailedBreakdown}
            disabled={loadingBreakdown}
            className="bg-blue-500 hover:bg-blue-700 disabled:bg-blue-300 text-white font-bold py-2 px-4 rounded"
          >
            {loadingBreakdown ? 'Refreshing...' : 'Refresh'}
          </button>
          <button
            onClick={handleGarbageCollection}
            disabled={gcTriggering}
            className="bg-red-500 hover:bg-red-700 disabled:bg-red-300 text-white font-bold py-2 px-4 rounded"
          >
            {gcTriggering ? 'Triggering GC...' : 'Trigger GC'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Warnings and Alerts Section */}
      {detailedBreakdown?.breakdown?.warnings && detailedBreakdown.breakdown.warnings.length > 0 && (
        <div className="mb-6">
          <h4 className="text-md font-medium text-gray-700 mb-3">⚠️ Critical Alerts</h4>
          <div className="space-y-2">
            {detailedBreakdown.breakdown.warnings.map((warning, index) => (
              <div key={index} className={`border rounded-lg p-3 ${getAlertLevel(warning.level)}`}>
                <span className="font-semibold">
                  {warning.level === 'critical' ? '🔴' : '🟡'} {warning.message}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Memory Overview Cards with detailed breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Total RAM Used</h4>
          <p className="text-xl font-bold text-red-600">
            {detailedBreakdown?.breakdown?.usedRAM
              ? `${detailedBreakdown.breakdown.usedRAM} MB`
              : formatBytes(memoryStats?.process?.memory?.rss || 0)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            of {detailedBreakdown?.breakdown?.totalRAM || '512'} MB
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h4 className="text-sm font-medium text-gray-500 mb-2">TD.binlog Size</h4>
          <p className="text-xl font-bold text-orange-600">
            {detailedBreakdown?.breakdown?.components?.tdlibSessions || '0'} MB
          </p>
          <p className="text-xs text-gray-500 mt-1">
            {detailedBreakdown?.breakdown?.sessionDetails?.accountCount || 0} accounts
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Database Pools</h4>
          <p className="text-xl font-bold text-yellow-600">
            {detailedBreakdown?.breakdown?.poolDetails?.count || '0'} pools
          </p>
          <p className="text-xs text-gray-500 mt-1">
            ~{detailedBreakdown?.breakdown?.components?.databasePools || '0'} MB
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h4 className="text-sm font-medium text-gray-500 mb-2">Node Heap</h4>
          <p className="text-xl font-bold text-blue-600">
            {detailedBreakdown?.breakdown?.components?.nodeHeap
              ? `${detailedBreakdown.breakdown.components.nodeHeap} MB`
              : formatBytes(memoryStats?.process?.memory?.heapUsed || 0)}
          </p>
          <p className="text-xs text-gray-500 mt-1">
            JavaScript memory
          </p>
        </div>
      </div>

      {/* Two column layout for charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Pie Chart - Memory Component Breakdown */}
        <div className="bg-white rounded-lg shadow p-4">
          <h4 className="text-md font-medium text-gray-700 mb-3">Memory Component Breakdown</h4>
          {preparePieChartData() ? (
            <Pie
              data={preparePieChartData()}
              options={{
                responsive: true,
                plugins: {
                  legend: {
                    position: 'bottom'
                  },
                  tooltip: {
                    callbacks: {
                      label: function(context) {
                        const label = context.label || '';
                        const value = context.parsed || 0;
                        const total = context.dataset.data.reduce((a, b) => a + b, 0);
                        const percentage = ((value / total) * 100).toFixed(1);
                        return `${label}: ${value} MB (${percentage}%)`;
                      }
                    }
                  }
                }
              }}
            />
          ) : (
            <p className="text-gray-500 italic">Loading component breakdown...</p>
          )}
        </div>

        {/* Bar Chart - Top Memory Consumers */}
        <div className="bg-white rounded-lg shadow p-4">
          <h4 className="text-md font-medium text-gray-700 mb-3">Top Memory Consumers (Accounts)</h4>
          {prepareBarChartData() ? (
            <Bar
              data={prepareBarChartData()}
              options={{
                responsive: true,
                scales: {
                  y: {
                    beginAtZero: true,
                    title: {
                      display: true,
                      text: 'Memory (MB)'
                    }
                  }
                },
                plugins: {
                  legend: {
                    position: 'top'
                  }
                }
              }}
            />
          ) : (
            <p className="text-gray-500 italic">No account data available</p>
          )}
        </div>
      </div>

      {/* Component Growth History Chart */}
      <div className="mb-6">
        <h4 className="text-md font-medium text-gray-700 mb-3">Memory Growth by Component (Time Series)</h4>
        {prepareComponentHistoryChart() ? (
          <Line
            data={prepareComponentHistoryChart()}
            options={{
              responsive: true,
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Memory (MB)'
                  }
                },
                x: {
                  title: {
                    display: true,
                    text: 'Time (samples ago)'
                  }
                }
              },
              plugins: {
                legend: {
                  position: 'top'
                },
                title: {
                  display: true,
                  text: 'Shows which components are growing over time'
                }
              }
            }}
          />
        ) : memoryStats?.history && memoryStats.history.length > 0 ? (
          <Line
            data={prepareMemoryChartData(memoryStats)}
            options={{
              responsive: true,
              scales: {
                y: {
                  beginAtZero: true,
                  title: {
                    display: true,
                    text: 'Memory (MB)'
                  }
                }
              }
            }}
          />
        ) : (
          <p className="text-gray-500 italic">No history data available</p>
        )}
      </div>

      {/* Detailed Table of Top Memory Consumers */}
      {detailedBreakdown?.breakdown?.topMemoryConsumers && detailedBreakdown.breakdown.topMemoryConsumers.length > 0 && (
        <div className="mb-6">
          <h4 className="text-md font-medium text-gray-700 mb-3">Detailed Account Memory Usage</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Account</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">API ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Size</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">TD.binlog</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {detailedBreakdown.breakdown.topMemoryConsumers.map((consumer, index) => (
                  <tr key={index}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {consumer.account}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {consumer.apiId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {consumer.sizeMB} MB
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {consumer.binlogMB} MB
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadge(consumer.status)}`}>
                        {consumer.status === 'critical' ? '⚠️ Critical' :
                         consumer.status === 'warning' ? '⚠️ Warning' : '✅ OK'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {detailedBreakdown?.breakdown?.recommendations && detailedBreakdown.breakdown.recommendations.length > 0 && (
        <div className="mb-6">
          <h4 className="text-md font-medium text-gray-700 mb-3">📋 Recommendations</h4>
          <ul className="list-disc list-inside space-y-1">
            {detailedBreakdown.breakdown.recommendations.map((rec, index) => (
              <li key={index} className="text-sm text-gray-600">{rec}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Additional Diagnostics Section */}
      {detailedBreakdown?.breakdown?.additionalDiagnostics && (
        <div className="mb-6">
          <h4 className="text-md font-medium text-gray-700 mb-3">🔍 Advanced Memory Diagnostics</h4>

          {/* Suspicious Patterns */}
          {detailedBreakdown.breakdown.additionalDiagnostics.suspiciousPatterns?.length > 0 && (
            <div className="mb-4">
              <h5 className="text-sm font-medium text-gray-600 mb-2">Detected Issues:</h5>
              <div className="space-y-2">
                {detailedBreakdown.breakdown.additionalDiagnostics.suspiciousPatterns.map((pattern, idx) => (
                  <div key={idx} className="bg-yellow-50 border border-yellow-200 rounded p-2">
                    <span className="text-sm text-yellow-800">
                      ⚠️ {pattern.message} ({pattern.value} MB)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Largest Memory Consumers */}
          {detailedBreakdown.breakdown.additionalDiagnostics.largestObjects?.length > 0 && (
            <div className="mb-4">
              <h5 className="text-sm font-medium text-gray-600 mb-2">Largest Memory Consumers:</h5>
              <div className="bg-gray-50 rounded p-3">
                <table className="min-w-full">
                  <tbody>
                    {detailedBreakdown.breakdown.additionalDiagnostics.largestObjects.map((obj, idx) => (
                      <tr key={idx} className="border-b border-gray-200 last:border-0">
                        <td className="py-1 text-sm text-gray-700">{obj.name}</td>
                        <td className="py-1 text-sm font-bold text-right">{obj.estimatedMB} MB</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Estimated TDLib Memory */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-blue-50 rounded p-3">
              <h5 className="text-xs text-blue-600 mb-1">TDLib Native Memory (C++)</h5>
              <p className="text-xl font-bold text-blue-800">
                {detailedBreakdown.breakdown.additionalDiagnostics.tdlibNativeMemory || 0} MB
              </p>
              <p className="text-xs text-blue-600">~30MB per client × {detailedBreakdown.breakdown.globalObjects?.tdlibClients || 0} clients</p>
            </div>
            <div className="bg-purple-50 rounded p-3">
              <h5 className="text-xs text-purple-600 mb-1">Total TDLib Estimate</h5>
              <p className="text-xl font-bold text-purple-800">
                {detailedBreakdown.breakdown.additionalDiagnostics.estimatedTDLibMemory || 0} MB
              </p>
              <p className="text-xs text-purple-600">~50MB per client total</p>
            </div>
          </div>
        </div>
      )}

      {/* Global Objects Info */}
      {detailedBreakdown?.breakdown?.globalObjects && (
        <div className="mb-6">
          <h4 className="text-md font-medium text-gray-700 mb-3">Global Objects Status</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {Object.entries(detailedBreakdown.breakdown.globalObjects).map(([key, value]) => (
              <div key={key} className="bg-gray-50 rounded p-2">
                <span className="text-xs text-gray-500">{key}:</span>
                <span className="ml-2 text-sm font-medium">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* TDLib Clients Table (original) */}
      {memoryStats?.clients && memoryStats.clients.tracked && memoryStats.clients.tracked.length > 0 && (
        <div className="mt-6">
          <h4 className="text-md font-medium text-gray-700 mb-3">Active TDLib Clients ({memoryStats.clients.count} total)</h4>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">API ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {memoryStats.clients.tracked.slice(0, 10).map((apiId) => (
                  <tr key={apiId}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {apiId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                        Active
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {memoryStats.clients.tracked.length > 10 && (
              <p className="text-sm text-gray-500 mt-2 px-6">
                And {memoryStats.clients.tracked.length - 10} more...
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MemoryMonitorTab;